import { FC, useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { useSnackbar } from 'notistack'
import dayjs from 'dayjs'
import { EstadoDeLista } from '@/components/common/EstadoDeLista'
import { competenciaService } from '@/services/competenciaService'
import {
  CompetenciaComissao,
  CompetenciaDetalhe,
  CompetenciasComissaoResponse,
  MesCompetencia,
} from '@/types/competencia'
import { brl } from './estadoFinanceiro'
import { statusDoErro } from './errosNotaFiscal'
import FecharCompetenciaDialog, { mesCompetencia } from './FecharCompetenciaDialog'

const dataHora = (iso?: string | null) => (iso ? dayjs(iso).format('DD/MM/YYYY HH:mm') : '—')

/** O backend valida (422 `MOTIVO_REABERTURA_INVALIDO`); a tela só não manda o que ele vai recusar. */
const MOTIVO_MIN = 10
const MOTIVO_MAX = 1000

interface Props {
  /** FINANCEIRO ou ADMIN (o POST confere de novo). O botão aparece só onde o backend disse `podeFechar`. */
  podeFechar: boolean
  /** Só ADMIN. O botão aparece só onde o backend disse `podeReabrir`. */
  podeReabrir: boolean
}

type Carga =
  | { estado: 'carregando' }
  | { estado: 'ok'; dados: CompetenciasComissaoResponse }
  | { estado: 'erro'; mensagem: string }
  | { estado: 'semPermissao' }

/**
 * Aba "Competências" do Financeiro (AN-PP5 §5, §9): meses com estado (Aberta/Fechada), versão do
 * fechamento e totais **do backend** (snapshot quando fechada por fechamento próprio, senão ao vivo).
 * Fechar (FINANCEIRO/ADMIN) e reabrir (só ADMIN, com motivo) obedecem `podeFechar`/`podeReabrir`
 * que o backend calcula por competência. "Detalhe" mostra o histórico de atos com o snapshot.
 *
 * **Nada é calculado aqui**: nem totais, nem a sequência, nem a marca d'água.
 */
const CompetenciasPanel: FC<Props> = ({ podeFechar, podeReabrir }) => {
  const { enqueueSnackbar } = useSnackbar()
  const [carga, setCarga] = useState<Carga>({ estado: 'carregando' })
  const [fechando, setFechando] = useState<MesCompetencia | null>(null)
  const [reabrindo, setReabrindo] = useState<MesCompetencia | null>(null)
  const [motivo, setMotivo] = useState('')
  const [erroReabrir, setErroReabrir] = useState<string | null>(null)
  const [salvando, setSalvando] = useState(false)
  const [detalhe, setDetalhe] = useState<{ competencia: MesCompetencia; dados: CompetenciaDetalhe | null; erro: string | null } | null>(null)

  const carregar = useCallback(async () => {
    setCarga({ estado: 'carregando' })
    try {
      setCarga({ estado: 'ok', dados: await competenciaService.listar() })
    } catch (e) {
      setCarga(
        statusDoErro(e) === 403
          ? { estado: 'semPermissao' }
          : { estado: 'erro', mensagem: (e as { message?: string })?.message ?? 'Falha ao carregar as competências.' }
      )
    }
  }, [])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const abrirDetalhe = (competencia: MesCompetencia) => {
    setDetalhe({ competencia, dados: null, erro: null })
    competenciaService
      .detalhar(competencia)
      .then((dados) => setDetalhe((d) => (d && d.competencia === competencia ? { ...d, dados } : d)))
      .catch((e) =>
        setDetalhe((d) =>
          d && d.competencia === competencia
            ? { ...d, erro: (e as { message?: string })?.message ?? 'Falha ao carregar o detalhe.' }
            : d
        )
      )
  }

  const reabrir = async () => {
    if (!reabrindo) return
    setSalvando(true)
    setErroReabrir(null)
    try {
      await competenciaService.reabrir(reabrindo, motivo.trim())
      enqueueSnackbar(`Competência ${mesCompetencia(reabrindo)} reaberta.`, { variant: 'success' })
      setReabrindo(null)
      await carregar()
    } catch (e) {
      setErroReabrir((e as { message?: string })?.message ?? 'Falha ao reabrir a competência.')
    } finally {
      setSalvando(false)
    }
  }

  if (carga.estado === 'carregando') return <EstadoDeLista estado="carregando" minHeight={160} />
  if (carga.estado === 'semPermissao') return <EstadoDeLista estado="semPermissao" minHeight={160} />
  if (carga.estado === 'erro') {
    return (
      <EstadoDeLista
        estado="erro"
        mensagem="Não foi possível carregar as competências"
        detalhe={carga.mensagem}
        acao={{ label: 'Tentar novamente', onClick: () => void carregar() }}
        minHeight={160}
      />
    )
  }

  const { dados } = carga
  const motivoValido = motivo.trim().length >= MOTIVO_MIN && motivo.trim().length <= MOTIVO_MAX

  return (
    <Stack spacing={2}>
      <Alert severity="info">
        {dados.fechadaAte
          ? `Competências fechadas até ${mesCompetencia(dados.fechadaAte)}. Próxima a fechar: ${mesCompetencia(dados.primeiraAberta)}.`
          : 'Nenhuma competência fechada ainda: o primeiro fechamento fecha também todas as competências anteriores.'}{' '}
        Depois de fechada, a competência não muda: ajustes caem na competência corrente.
      </Alert>

      {dados.competencias.length === 0 ? (
        <EstadoDeLista estado="vazio" mensagem="Nenhuma competência no período" minHeight={120} />
      ) : (
        <Card>
          <TableContainer sx={{ overflowX: 'auto' }}>
            <Table size="small" aria-label="Competências">
              <TableHead>
                <TableRow>
                  <TableCell>Competência</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Versão</TableCell>
                  <TableCell align="right">Lançamentos</TableCell>
                  <TableCell align="right">Total confirmado</TableCell>
                  <TableCell align="right">Receita líquida</TableCell>
                  <TableCell align="right">Projetadas</TableCell>
                  <TableCell>Fechada em</TableCell>
                  <TableCell align="right">Ações</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dados.competencias.map((c: CompetenciaComissao) => (
                  <TableRow key={c.competencia} data-testid={`competencia-${c.competencia}`}>
                    <TableCell sx={{ fontWeight: 600 }}>{mesCompetencia(c.competencia)}</TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap">
                        <Chip
                          size="small"
                          label={c.estado === 'FECHADA' ? 'Fechada' : 'Aberta'}
                          color={c.estado === 'FECHADA' ? 'default' : 'success'}
                        />
                        {c.emCurso && <Chip size="small" variant="outlined" label="Em curso" />}
                        {c.reaberturas > 0 && (
                          <Chip size="small" variant="outlined" color="warning" label={`Reaberta ${c.reaberturas}x`} />
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{c.versaoFechamento != null ? `v${c.versaoFechamento}` : '—'}</TableCell>
                    <TableCell align="right">{c.qtdLancamentos}</TableCell>
                    <TableCell align="right">{brl(c.totalConfirmado)}</TableCell>
                    <TableCell align="right">{brl(c.totalReceitaLiquida)}</TableCell>
                    <TableCell align="right">{c.qtdProjetadas}</TableCell>
                    <TableCell>
                      {dataHora(c.fechadoEm)}
                      <Typography variant="caption" color="textSecondary" display="block">
                        {c.origemTotais === 'SNAPSHOT' ? 'Totais do fechamento' : 'Totais ao vivo'}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <Button size="small" onClick={() => abrirDetalhe(c.competencia)}>
                          Detalhe
                        </Button>
                        {podeFechar && c.podeFechar && (
                          <Button size="small" variant="contained" color="warning" onClick={() => setFechando(c.competencia)}>
                            Fechar
                          </Button>
                        )}
                        {podeReabrir && c.podeReabrir && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setReabrindo(c.competencia)
                              setMotivo('')
                              setErroReabrir(null)
                            }}
                          >
                            Reabrir
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      )}

      <FecharCompetenciaDialog
        competencia={fechando}
        semFechamentoAinda={dados.semFechamentoAinda}
        onFechar={() => setFechando(null)}
        onFechada={(r) => {
          enqueueSnackbar(
            `Competência ${mesCompetencia(r.competencia)} fechada (versão ${r.versao ?? '—'})` +
              (r.fechamentoInicial ? ' — as competências anteriores também ficaram fechadas.' : '.'),
            { variant: 'success' }
          )
          setFechando(null)
          void carregar()
        }}
      />

      <Dialog open={reabrindo !== null} onClose={() => setReabrindo(null)} fullWidth maxWidth="sm">
        <DialogTitle>Reabrir competência {mesCompetencia(reabrindo)}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            {erroReabrir && <Alert severity="error">{erroReabrir}</Alert>}
            <Typography variant="body2">
              Só a última competência fechada reabre. O fechamento atual fica guardado; o próximo
              fechamento grava uma nova versão. O consultor vê que a competência foi reaberta.
            </Typography>
            <TextField
              label="Motivo da reabertura"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              multiline
              minRows={2}
              inputProps={{ maxLength: MOTIVO_MAX }}
              helperText={`De ${MOTIVO_MIN} a ${MOTIVO_MAX} caracteres. Fica na auditoria.`}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReabrindo(null)}>Voltar</Button>
          <Button variant="contained" onClick={() => void reabrir()} disabled={salvando || !motivoValido}>
            {salvando ? 'Reabrindo…' : 'Reabrir competência'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={detalhe !== null} onClose={() => setDetalhe(null)} fullWidth maxWidth="md">
        <DialogTitle>Competência {mesCompetencia(detalhe?.competencia)}</DialogTitle>
        <DialogContent dividers>
          {detalhe?.erro ? (
            <Alert severity="error">{detalhe.erro}</Alert>
          ) : !detalhe?.dados ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
              <CircularProgress aria-label="Carregando detalhe" />
            </Box>
          ) : detalhe.dados.historico.length === 0 ? (
            <Typography variant="body2" color="textSecondary">
              Nenhum fechamento registrado para esta competência
              {detalhe.dados.estado === 'FECHADA' ? ' (fechada pelo fechamento inicial de um mês posterior).' : '.'}
            </Typography>
          ) : (
            <Stack spacing={2}>
              {detalhe.dados.historico.map((ato) => (
                <Box key={ato.id}>
                  <Typography variant="subtitle2">
                    {ato.acao === 'FECHAMENTO' ? 'Fechamento' : 'Reabertura'} v{ato.versao ?? '—'} ·{' '}
                    {dataHora(ato.executadoEm)}
                  </Typography>
                  {ato.motivo && (
                    <Typography variant="body2" color="textSecondary">
                      Motivo: {ato.motivo}
                    </Typography>
                  )}
                  {ato.acao === 'FECHAMENTO' && (
                    <Typography variant="body2">
                      {ato.qtdLancamentos ?? 0} lançamento(s) · confirmado {brl(ato.totalConfirmado)} · receita líquida{' '}
                      {brl(ato.totalReceitaLiquida)}
                    </Typography>
                  )}
                  {ato.itens && ato.itens.length > 0 && (
                    <Table size="small" aria-label={`Itens do fechamento v${ato.versao ?? ''}`}>
                      <TableHead>
                        <TableRow>
                          <TableCell>Reserva</TableCell>
                          <TableCell>Cliente</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Valor</TableCell>
                          <TableCell align="right">Receita líquida</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {ato.itens.map((i) => (
                          <TableRow key={i.lancamentoId}>
                            <TableCell>{i.reservaNumero != null ? `RES-${i.reservaNumero}` : '—'}</TableCell>
                            <TableCell>{i.razaoSocial ?? '—'}</TableCell>
                            <TableCell>{i.tipo === 'ESTORNO' ? 'Estorno' : 'Lançamento'}</TableCell>
                            <TableCell>{i.status === 'CANCELADA' ? 'Cancelada' : 'Confirmada'}</TableCell>
                            <TableCell align="right">{brl(i.valorEfetivo)}</TableCell>
                            <TableCell align="right">{brl(i.receitaLiquida)}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                  {ato.ajustes && ato.ajustes.length > 0 && (
                    <Typography variant="caption" color="textSecondary" display="block">
                      Ajustes de receita no fechamento: {ato.ajustes.map((a) => brl(a.valor)).join(' · ')}
                    </Typography>
                  )}
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetalhe(null)}>Voltar</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  )
}

export default CompetenciasPanel
